import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Envase.css';

// Emitir un evento global para que el header refresque el logo tras un cambio
const LOGO_EVENT = 'cgctrl-logo-updated';
const emitirLogoActualizado = () => window.dispatchEvent(new Event(LOGO_EVENT));

const formularioInicial = () => ({
    idEmpresa: 1,
    empresa: '',
    sistema: '',
    ciudad: '',
    telefono: '',
    ruc: '',
    ramo: '',
    direccion: '',
    gerente: '',
    contador: '',
    ruccontador: '',
    inicioejercicio: '',
    finejercicio: '',
    ctaresanterior: '',
    ctaresactual: '',
    cginiciomestrab: '',
    cgfinmestrab: '',
    stkiniciomestrab: '',
    stkfinmestrab: '',
    vtainiciomestrab: '',
    vtafinmestrab: '',
    tesiniciomestrab: '',
    tesfinmestrab: '',
    sueiniciomestrab: '',
    suefinmestrab: '',
    ctarecaudacion: '',
    ctacheque: '',
    ctaivacompra: '',
    ctaivaventa: '',
    ctavtagravada: '',
    ctavtaexenta: '',
    ctavtaotros: '',
    ctaretencion: '',
    logoBg: ''
});

// Presets de fondo comunes (transparent + comunes que combinan con headers oscuros/claros)
const PRESETS_LOGO_BG = [
    { valor: '',           nombre: 'Transparente', muestra: 'transparent' },
    { valor: '#FFFFFF',    nombre: 'Blanco',        muestra: '#FFFFFF' },
    { valor: '#F5F5F5',    nombre: 'Gris claro',   muestra: '#F5F5F5' },
    { valor: '#004F9F',    nombre: 'Azul header',   muestra: '#004F9F' },
    { valor: '#000000',    nombre: 'Negro',         muestra: '#000000' }
];

// Campos de cuenta contable (agrupados con etiqueta y ayuda)
const CAMPOS_CUENTA = [
    { key: 'ctaresanterior',  label: 'Resultado ejercicio anterior' },
    { key: 'ctaresactual',    label: 'Resultado ejercicio actual' },
    { key: 'ctarecaudacion',  label: 'Recaudacion (caja)' },
    { key: 'ctacheque',       label: 'Cheques recibidos' },
    { key: 'ctaivacompra',    label: 'IVA Compra' },
    { key: 'ctaivaventa',     label: 'IVA Venta' },
    { key: 'ctavtagravada',   label: 'Venta Gravada' },
    { key: 'ctavtaexenta',    label: 'Venta Exenta' },
    { key: 'ctavtaotros',     label: 'Venta Otros' },
    { key: 'ctaretencion',    label: 'Retencion' }
];

const MODULOS = [
    { key: 'cg',  label: 'Contabilidad',       ini: 'cginiciomestrab',  fin: 'cgfinmestrab'  },
    { key: 'stk', label: 'Stock',              ini: 'stkiniciomestrab', fin: 'stkfinmestrab' },
    { key: 'vta', label: 'Ventas',             ini: 'vtainiciomestrab', fin: 'vtafinmestrab' },
    { key: 'tes', label: 'Tesoreria',          ini: 'tesiniciomestrab', fin: 'tesfinmestrab' },
    { key: 'sue', label: 'Sueldos',            ini: 'sueiniciomestrab', fin: 'suefinmestrab' }
];

// ============================================================
// Buscador incremental de cuentas contables
// - Muestra la cuenta seleccionada (codigo + descripcion) como chip
// - Al hacer clic en "cambiar" o si no hay valor, se abre input de busqueda
// - Debounce 250ms, dropdown con hasta 40 resultados
// - Si el usuario tipea un codigo exacto y presiona Enter, lo selecciona directo
// ============================================================
function BuscadorCuenta({ campo, label, valor, deshabilitado, cuentasCatalogo, onSeleccionar }) {
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState([]);
    const [abierto, setAbierto] = useState(false);
    const [descCache, setDescCache] = useState(null);
    const containerRef = useRef(null);

    // Traer la descripcion cuando cambia el valor externo (para mostrarla)
    useEffect(() => {
        if (!valor) { setDescCache(null); return; }
        // Primero, buscar en el catalogo local (mas rapido)
        const local = cuentasCatalogo.find(c =>
            String(c.cuenta).trim() === String(valor).trim());
        if (local) { setDescCache(local.descripcion); return; }
        // Sino, pedir al backend (por si la cuenta no es imputable pero
        // esta asignada, o el catalogo aun no cargo)
        axios.get('/api/cgctrl/cuentas/buscar', { params: { busqueda: valor } })
            .then(({ data }) => {
                const exact = data.find(c => String(c.cuenta).trim() === String(valor).trim());
                setDescCache(exact ? exact.descripcion : null);
            })
            .catch(() => setDescCache(null));
    }, [valor, cuentasCatalogo]);

    // Busqueda con debounce
    useEffect(() => {
        if (!abierto) return;
        if (busqueda.length < 1) {
            // Sin texto: mostrar todo el catalogo imputable (hasta 40)
            setResultados(cuentasCatalogo.slice(0, 40));
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/cgctrl/cuentas/buscar',
                    { params: { busqueda } });
                setResultados(data);
            } catch (e) { console.error(e); setResultados([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [busqueda, abierto, cuentasCatalogo]);

    // Cerrar dropdown al clic fuera
    useEffect(() => {
        if (!abierto) return;
        const handler = (ev) => {
            if (containerRef.current && !containerRef.current.contains(ev.target)) {
                setAbierto(false);
                setBusqueda('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [abierto]);

    const seleccionar = (c) => {
        onSeleccionar(c.cuenta);
        setDescCache(c.descripcion);
        setAbierto(false);
        setBusqueda('');
    };

    const limpiar = () => {
        onSeleccionar('');
        setDescCache(null);
        setBusqueda('');
    };

    const onEnterBuscar = async (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const q = busqueda.trim();
        if (!q) return;
        try {
            const { data } = await axios.get('/api/cgctrl/cuentas/buscar', { params: { busqueda: q } });
            const exact = data.find(c => String(c.cuenta).trim() === q);
            if (exact) seleccionar(exact);
            else if (data.length === 1) seleccionar(data[0]);
            else setResultados(data);
        } catch (err) { console.error(err); }
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '2px' }}>
                {label}
            </label>

            {/* Estado con valor seleccionado */}
            {valor && !abierto ? (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px',
                    background: deshabilitado ? '#F5F5F5' : '#E4F2FC',
                    fontSize: '13px', minHeight: '30px'
                }}>
                    <strong style={{ color: '#004F9F' }}>{String(valor).trim()}</strong>
                    <span style={{ color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {descCache || '...'}
                    </span>
                    {!deshabilitado && (
                        <>
                            <button type="button" onClick={() => setAbierto(true)}
                                title="Cambiar"
                                style={{
                                    background: 'none', border: 'none', color: '#004F9F',
                                    cursor: 'pointer', fontSize: '11px', fontWeight: '600'
                                }}>✎</button>
                            <button type="button" onClick={limpiar}
                                title="Quitar"
                                style={{
                                    background: 'none', border: 'none', color: '#C62828',
                                    cursor: 'pointer', fontSize: '13px', padding: 0
                                }}>✕</button>
                        </>
                    )}
                </div>
            ) : (
                // Input de busqueda
                <div style={{ position: 'relative' }}>
                    <input type="text"
                        placeholder={deshabilitado ? '— sin asignar —' : 'Buscar cuenta (codigo o descripcion)...'}
                        value={busqueda}
                        onFocus={() => !deshabilitado && setAbierto(true)}
                        onChange={(e) => { setBusqueda(e.target.value); setAbierto(true); }}
                        onKeyDown={onEnterBuscar}
                        disabled={deshabilitado}
                        style={{
                            width: '100%', padding: '6px 8px',
                            border: '1px solid #ddd', borderRadius: '4px',
                            background: deshabilitado ? '#F5F5F5' : '#fff',
                            fontSize: '13px'
                        }} />
                    {abierto && resultados.length > 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            background: 'white', border: '1px solid #004F9F',
                            borderRadius: '4px', marginTop: '2px',
                            maxHeight: '260px', overflowY: 'auto',
                            zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}>
                            {resultados.map(c => (
                                <div key={c.cuenta}
                                    onClick={() => seleccionar(c)}
                                    style={{
                                        padding: '6px 10px', cursor: 'pointer',
                                        borderBottom: '1px solid #eee', fontSize: '13px',
                                        display: 'flex', gap: '8px', alignItems: 'center'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                    <strong style={{ color: '#004F9F', minWidth: '80px' }}>
                                        {String(c.cuenta).trim()}
                                    </strong>
                                    <span style={{ color: '#333', flex: 1 }}>{c.descripcion}</span>
                                    {c.imputable === 'N' && (
                                        <span style={{
                                            fontSize: '10px', padding: '1px 6px', borderRadius: '8px',
                                            background: '#FFF3E0', color: '#E65100'
                                        }}>agrupadora</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {abierto && busqueda.length > 0 && resultados.length === 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            background: 'white', border: '1px solid #ddd',
                            padding: '8px', color: '#888', fontSize: '12px',
                            borderRadius: '4px', marginTop: '2px', zIndex: 100
                        }}>Sin resultados</div>
                    )}
                </div>
            )}
        </div>
    );
}

function CGCtrl({ usuario }) {
    const [formulario, setFormulario] = useState(formularioInicial());
    const [cuentas, setCuentas] = useState([]);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [existeRegistro, setExisteRegistro] = useState(false);
    const [msg, setMsg] = useState('');

    // Logo de la empresa
    // logoVersion: cache-buster, se incrementa al subir/eliminar
    const [logoVersion, setLogoVersion] = useState(Date.now());
    const [logoExiste, setLogoExiste] = useState(false);
    const [subiendoLogo, setSubiendoLogo] = useState(false);
    const inputLogoRef = useRef(null);

    useEffect(() => {
        cargar();
        cargarCuentas();
        verificarLogo();
    }, []);

    const verificarLogo = async () => {
        try {
            await axios.head('/api/cgctrl/logo');
            setLogoExiste(true);
        } catch {
            setLogoExiste(false);
        }
    };

    const seleccionarArchivoLogo = () => inputLogoRef.current?.click();

    const onArchivoLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert('El archivo debe ser una imagen');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            alert('El logo excede 2 MB');
            return;
        }
        setSubiendoLogo(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post('/api/cgctrl/logo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setLogoVersion(Date.now());
            setLogoExiste(true);
            emitirLogoActualizado();
            setMsg('Logo actualizado correctamente.');
        } catch (err) {
            alert(err.response?.data?.message || 'Error al subir el logo');
        } finally {
            setSubiendoLogo(false);
            if (inputLogoRef.current) inputLogoRef.current.value = '';
        }
    };

    const eliminarLogo = async () => {
        if (!window.confirm('Eliminar el logo actual? Se volvera al logo por defecto.')) return;
        try {
            await axios.delete('/api/cgctrl/logo');
            setLogoExiste(false);
            setLogoVersion(Date.now());
            emitirLogoActualizado();
            setMsg('Logo eliminado.');
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar el logo');
        }
    };

    const cargar = async () => {
        try {
            const { data, status } = await axios.get('/api/cgctrl',
                { validateStatus: (s) => s < 400 || s === 204 });
            if (status === 204 || !data) {
                setExisteRegistro(false);
                setModoEdicion(true);
                setMsg('No hay configuracion cargada. Complete los datos.');
                return;
            }
            // Convertir null a '' para inputs controlados
            const norm = {};
            Object.keys(formularioInicial()).forEach(k => {
                norm[k] = data[k] == null ? '' : data[k];
            });
            setFormulario(norm);
            setExisteRegistro(true);
            setMsg('');
        } catch (e) {
            console.error(e);
            setMsg('Error al leer configuracion');
        }
    };

    const cargarCuentas = async () => {
        try {
            const { data } = await axios.get('/api/cgctrl/cuentas');
            setCuentas(data);
        } catch (e) {
            console.error('Error al cargar cuentas contables:', e);
            setCuentas([]);
        }
    };

    const handleChange = (campo, valor) => {
        setFormulario(f => ({ ...f, [campo]: valor }));
    };

    const guardar = async (e) => {
        e.preventDefault();
        if (!formulario.empresa.trim()) return alert('Nombre de la empresa es obligatorio');
        if (!formulario.ruc.trim())     return alert('RUC es obligatorio');
        setGuardando(true);
        try {
            const payload = { ...formulario };
            // Los campos '' que corresponden a fechas o cuentas se envian como null
            Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
            await axios.put('/api/cgctrl', payload);
            setMsg('Configuracion guardada correctamente.');
            setExisteRegistro(true);
            setModoEdicion(false);
            cargar();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar');
        } finally {
            setGuardando(false);
        }
    };

    const cancelar = () => {
        if (existeRegistro) {
            cargar();
            setModoEdicion(false);
            setMsg('');
        }
    };

    // Selector de cuenta con busqueda incremental
    const SelectCuenta = ({ campo, label }) => (
        <BuscadorCuenta
            campo={campo}
            label={label}
            valor={formulario[campo]}
            deshabilitado={!modoEdicion}
            cuentasCatalogo={cuentas}
            onSeleccionar={(cuenta) => handleChange(campo, cuenta)}
        />
    );

    const inputStyle = {
        width: '100%', padding: '6px 8px',
        border: '1px solid #ddd', borderRadius: '4px',
        background: modoEdicion ? '#fff' : '#F5F5F5',
        fontSize: '13px'
    };

    return (
        <div style={{ padding: '20px', fontFamily: 'Gilam, sans-serif', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: '#004F9F', margin: 0 }}>Configuracion de Empresa</h2>
                <div>
                    {!modoEdicion && existeRegistro && (
                        <button onClick={() => setModoEdicion(true)} style={{
                            padding: '8px 16px', background: '#004F9F', color: 'white',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
                        }}>✎ Editar</button>
                    )}
                </div>
            </div>

            {msg && (
                <div style={{
                    padding: '10px 15px',
                    background: existeRegistro ? '#E8F5E9' : '#FFF3E0',
                    color:      existeRegistro ? '#2E7D32' : '#E65100',
                    border: '1px solid ' + (existeRegistro ? '#4CAF50' : '#FFB300'),
                    borderRadius: '4px', marginBottom: '15px', fontSize: '13px'
                }}>{msg}</div>
            )}

            <form onSubmit={guardar}>
                {/* ======================================================== */}
                {/* Logo de la empresa                                         */}
                {/* ======================================================== */}
                <section style={panelStyle}>
                    <h3 style={sectionTitleStyle}>Logo de la empresa</h3>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Preview con el color de fondo elegido */}
                        <div style={{
                            width: '180px', height: '110px',
                            border: '2px dashed #BDBDBD', borderRadius: '6px',
                            background: formulario.logoBg || 'transparent',
                            backgroundImage: !formulario.logoBg
                                ? 'linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)'
                                : undefined,
                            backgroundSize: !formulario.logoBg ? '16px 16px' : undefined,
                            backgroundPosition: !formulario.logoBg ? '0 0, 0 8px, 8px -8px, -8px 0' : undefined,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden', padding: '8px'
                        }}>
                            {logoExiste ? (
                                <img src={`/api/cgctrl/logo?v=${logoVersion}`}
                                    alt="Logo empresa"
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : (
                                <span style={{ color: '#888', fontSize: '12px', textAlign: 'center' }}>
                                    Sin logo personalizado.<br />Se muestra el logo por defecto.
                                </span>
                            )}
                        </div>

                        {/* Acciones */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input type="file" accept="image/*" ref={inputLogoRef}
                                onChange={onArchivoLogo} style={{ display: 'none' }} />
                            <button type="button" onClick={seleccionarArchivoLogo} disabled={subiendoLogo}
                                style={{
                                    padding: '10px 20px', background: '#004F9F', color: 'white',
                                    border: 'none', borderRadius: '4px', fontWeight: '600',
                                    cursor: subiendoLogo ? 'wait' : 'pointer',
                                    opacity: subiendoLogo ? 0.7 : 1
                                }}>
                                {subiendoLogo ? 'Subiendo...' : (logoExiste ? '📷 Cambiar logo' : '📷 Subir logo')}
                            </button>
                            {logoExiste && (
                                <button type="button" onClick={eliminarLogo} disabled={subiendoLogo}
                                    style={{
                                        padding: '8px 16px', background: '#FFF', color: '#C62828',
                                        border: '1px solid #C62828', borderRadius: '4px',
                                        cursor: 'pointer', fontSize: '12px'
                                    }}>
                                    🗑 Eliminar logo
                                </button>
                            )}
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: '200px', color: '#555', fontSize: '12px', lineHeight: '1.6' }}>
                            <div><strong>Formatos aceptados:</strong> PNG, JPG, SVG, WebP, GIF</div>
                            <div><strong>Tamano maximo:</strong> 2 MB</div>
                            <div><strong>Sugerido:</strong> imagen con fondo transparente (PNG),<br />
                                proporcion horizontal (ej: 300×100 px)</div>
                            <div style={{ marginTop: '6px', color: '#004F9F' }}>
                                El logo aparece arriba a la izquierda del header, y en reportes.
                            </div>
                        </div>
                    </div>

                    {/* Color de fondo detras del logo (para logos con transparencia) */}
                    <div style={{ marginTop: '15px', paddingTop: '12px', borderTop: '1px dashed #ddd' }}>
                        <label style={{ fontSize: '13px', color: '#333', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                            Color de fondo detras del logo en el header
                        </label>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                            Si tu logo tiene fondo transparente y no se distingue en el header, elegi un color solido detras.
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {PRESETS_LOGO_BG.map(p => {
                                const seleccionado = (formulario.logoBg || '') === p.valor;
                                return (
                                    <button key={p.nombre} type="button"
                                        onClick={() => modoEdicion && handleChange('logoBg', p.valor)}
                                        disabled={!modoEdicion}
                                        title={p.nombre + (p.valor ? ` (${p.valor})` : '')}
                                        style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            padding: '4px', border: seleccionado ? '3px solid #004F9F' : '1px solid #ccc',
                                            borderRadius: '6px', cursor: modoEdicion ? 'pointer' : 'default',
                                            background: '#fff', opacity: modoEdicion ? 1 : 0.7
                                        }}>
                                        <div style={{
                                            width: '48px', height: '30px',
                                            background: p.muestra === 'transparent'
                                                ? 'linear-gradient(45deg, #ddd 25%, #fff 25%, #fff 50%, #ddd 50%, #ddd 75%, #fff 75%) 0 0 / 12px 12px'
                                                : p.muestra,
                                            border: '1px solid #ccc', borderRadius: '3px'
                                        }} />
                                        <span style={{ fontSize: '10px', color: '#555', marginTop: '3px' }}>{p.nombre}</span>
                                    </button>
                                );
                            })}
                            {/* Color picker libre */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '8px' }}>
                                <input type="color"
                                    value={formulario.logoBg && formulario.logoBg.startsWith('#') ? formulario.logoBg : '#FFFFFF'}
                                    onChange={(e) => handleChange('logoBg', e.target.value.toUpperCase())}
                                    disabled={!modoEdicion}
                                    style={{ width: '58px', height: '38px', padding: 0, cursor: modoEdicion ? 'pointer' : 'not-allowed',
                                             border: '1px solid #ccc', borderRadius: '4px', background: '#fff' }} />
                                <span style={{ fontSize: '10px', color: '#555', marginTop: '3px' }}>Personalizado</span>
                            </div>
                            {/* Muestra del valor actual */}
                            <div style={{ marginLeft: 'auto', padding: '8px 12px', background: '#F5F5F5',
                                          borderRadius: '4px', fontSize: '12px', color: '#333', minWidth: '140px' }}>
                                <div style={{ color: '#666', fontSize: '10px' }}>Valor actual:</div>
                                <code style={{ color: '#004F9F', fontWeight: '600' }}>
                                    {formulario.logoBg || 'transparent'}
                                </code>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ======================================================== */}
                {/* Datos de la empresa                                       */}
                {/* ======================================================== */}
                <section style={panelStyle}>
                    <h3 style={sectionTitleStyle}>Datos de la empresa</h3>
                    <div style={grid(4)}>
                        <div>
                            <label style={labelStyle}>Nombre de la empresa *</label>
                            <input type="text" value={formulario.empresa} required maxLength={40}
                                onChange={(e) => handleChange('empresa', e.target.value)}
                                readOnly={!modoEdicion} style={{ ...inputStyle, gridColumn: 'span 2' }} />
                        </div>
                        <div>
                            <label style={labelStyle}>RUC *</label>
                            <input type="text" value={formulario.ruc} required maxLength={15}
                                onChange={(e) => handleChange('ruc', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Sistema</label>
                            <input type="text" value={formulario.sistema} maxLength={20}
                                onChange={(e) => handleChange('sistema', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Ramo</label>
                            <input type="text" value={formulario.ramo} maxLength={20}
                                onChange={(e) => handleChange('ramo', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label style={labelStyle}>Direccion</label>
                        <input type="text" value={formulario.direccion} maxLength={90}
                            onChange={(e) => handleChange('direccion', e.target.value)}
                            readOnly={!modoEdicion} style={inputStyle} />
                    </div>
                    <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                            <label style={labelStyle}>Ciudad</label>
                            <input type="text" value={formulario.ciudad} maxLength={60}
                                onChange={(e) => handleChange('ciudad', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Telefono</label>
                            <input type="text" value={formulario.telefono} maxLength={40}
                                onChange={(e) => handleChange('telefono', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#888' }}>
                        Estos datos (nombre, RUC, direccion, ciudad, telefono) se imprimen en el encabezado de las facturas.
                    </div>
                </section>

                {/* ======================================================== */}
                {/* Personas                                                  */}
                {/* ======================================================== */}
                <section style={panelStyle}>
                    <h3 style={sectionTitleStyle}>Personal Directivo</h3>
                    <div style={grid(3)}>
                        <div>
                            <label style={labelStyle}>Gerente</label>
                            <input type="text" value={formulario.gerente} maxLength={30}
                                onChange={(e) => handleChange('gerente', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Contador</label>
                            <input type="text" value={formulario.contador} maxLength={30}
                                onChange={(e) => handleChange('contador', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>RUC Contador</label>
                            <input type="text" value={formulario.ruccontador} maxLength={15}
                                onChange={(e) => handleChange('ruccontador', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                    </div>
                </section>

                {/* ======================================================== */}
                {/* Ejercicio contable                                        */}
                {/* ======================================================== */}
                <section style={panelStyle}>
                    <h3 style={sectionTitleStyle}>Ejercicio Contable</h3>
                    <div style={grid(4)}>
                        <div>
                            <label style={labelStyle}>Inicio de ejercicio</label>
                            <input type="date" value={formulario.inicioejercicio || ''}
                                onChange={(e) => handleChange('inicioejercicio', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Fin de ejercicio</label>
                            <input type="date" value={formulario.finejercicio || ''}
                                onChange={(e) => handleChange('finejercicio', e.target.value)}
                                readOnly={!modoEdicion} style={inputStyle} />
                        </div>
                        <SelectCuenta campo="ctaresanterior" label="Cta. Resultado anterior" />
                        <SelectCuenta campo="ctaresactual"   label="Cta. Resultado actual" />
                    </div>
                </section>

                {/* ======================================================== */}
                {/* Meses de trabajo por modulo                               */}
                {/* ======================================================== */}
                <section style={panelStyle}>
                    <h3 style={sectionTitleStyle}>Meses de Trabajo por Modulo</h3>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                        Rangos de fechas abiertos para carga de movimientos en cada modulo del sistema.
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#E4F2FC' }}>
                            <tr>
                                <th style={{ padding: '6px', textAlign: 'left' }}>Modulo</th>
                                <th style={{ padding: '6px', textAlign: 'left' }}>Inicio mes trab.</th>
                                <th style={{ padding: '6px', textAlign: 'left' }}>Fin mes trab.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {MODULOS.map(m => (
                                <tr key={m.key} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '6px', fontWeight: '600', color: '#004F9F' }}>{m.label}</td>
                                    <td style={{ padding: '6px' }}>
                                        <input type="date" value={formulario[m.ini] || ''}
                                            onChange={(e) => handleChange(m.ini, e.target.value)}
                                            readOnly={!modoEdicion} style={inputStyle} />
                                    </td>
                                    <td style={{ padding: '6px' }}>
                                        <input type="date" value={formulario[m.fin] || ''}
                                            onChange={(e) => handleChange(m.fin, e.target.value)}
                                            readOnly={!modoEdicion} style={inputStyle} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>

                {/* ======================================================== */}
                {/* Cuentas contables clave                                   */}
                {/* ======================================================== */}
                <section style={panelStyle}>
                    <h3 style={sectionTitleStyle}>
                        Cuentas Contables Clave
                        <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal', marginLeft: '10px' }}>
                            {cuentas.length} cuentas imputables disponibles
                        </span>
                    </h3>
                    <div style={grid(3)}>
                        {CAMPOS_CUENTA.filter(c => c.key !== 'ctaresanterior' && c.key !== 'ctaresactual')
                            .map(c => <SelectCuenta key={c.key} campo={c.key} label={c.label} />)}
                    </div>
                </section>

                {/* Botones */}
                {modoEdicion && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                        {existeRegistro && (
                            <button type="button" onClick={cancelar} style={{
                                padding: '10px 20px', background: '#F5F5F5', color: '#555',
                                border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer'
                            }}>Cancelar</button>
                        )}
                        <button type="submit" disabled={guardando} style={{
                            padding: '10px 24px', background: '#004F9F', color: 'white',
                            border: 'none', borderRadius: '4px', cursor: guardando ? 'wait' : 'pointer',
                            fontWeight: '600', opacity: guardando ? 0.7 : 1
                        }}>{guardando ? 'Guardando...' : (existeRegistro ? 'Actualizar' : 'Crear configuracion')}</button>
                    </div>
                )}
            </form>
        </div>
    );
}

// Estilos comunes
const panelStyle = {
    marginBottom: '15px', padding: '15px',
    border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa'
};
const sectionTitleStyle = {
    margin: '0 0 12px 0', color: '#004F9F',
    borderBottom: '2px solid #004F9F', paddingBottom: '5px', fontSize: '15px'
};
const labelStyle = { fontSize: '12px', color: '#555', display: 'block', marginBottom: '2px' };
const grid = (cols) => ({
    display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '10px'
});

export default CGCtrl;
